// Mock events, СКО/ОСР commands and shift-handover data for R3.
// Timestamps are relative to `now` passed in (avoids stale fixtures).

export type EventType = 'inverter_fault' | 'link_lost' | 'curtailment' | 'op_safety' | 'planned' | 'manual';
export type Severity = 'alarm' | 'warning' | 'info';
export type EventStatus = 'active' | 'acknowledged' | 'cleared';

export interface SysEvent {
  id: string;
  ts: string;            // ISO
  type: EventType;
  severity: Severity;
  stationId: string;
  stationName: string;
  device?: string;
  message: string;
  status: EventStatus;
  parentId?: string;     // root-cause grouping (derived events point to parent)
  ackBy?: string;
}

export type CmdSource = 'lms' | 'email' | 'phone';
export type CmdKind = 'sko' | 'op_safety';
export type CmdStatus = 'pending' | 'acknowledged' | 'executing' | 'completed' | 'expired';

export interface SkoCommand {
  id: string;
  source: CmdSource;
  kind: CmdKind;
  stationId: string;
  stationName: string;
  limitPct: number;
  receivedAt: string;
  startAt: string;
  endAt: string | null;
  status: CmdStatus;
  fromPerson?: string;
  docRef?: string;
  compensated?: boolean;
}

const iso = (now: Date, minsAgo: number) => new Date(now.getTime() - minsAgo * 60000).toISOString();

export function mockEvents(now: Date = new Date()): SysEvent[] {
  const e: SysEvent[] = [
    // Root cause: link lost to Nickelios → child inverter-offline events suppressed under it
    { id: 'ev-100', ts: iso(now, 7), type: 'link_lost', severity: 'alarm', stationId: 'nickelios', stationName: 'Нікеліос', message: 'Втрачено звʼязок зі станцією (EMS BOX offline)', status: 'active' },
    { id: 'ev-101', ts: iso(now, 7), type: 'inverter_fault', severity: 'alarm', stationId: 'nickelios', stationName: 'Нікеліос', device: 'INV-12', message: 'Інвертор не відповідає', status: 'active', parentId: 'ev-100' },
    { id: 'ev-102', ts: iso(now, 7), type: 'inverter_fault', severity: 'alarm', stationId: 'nickelios', stationName: 'Нікеліос', device: 'INV-13', message: 'Інвертор не відповідає', status: 'active', parentId: 'ev-100' },
    { id: 'ev-103', ts: iso(now, 7), type: 'inverter_fault', severity: 'alarm', stationId: 'nickelios', stationName: 'Нікеліос', device: 'INV-14', message: 'Інвертор не відповідає', status: 'active', parentId: 'ev-100' },
    // Tabaki real inverter fault
    { id: 'ev-110', ts: iso(now, 95), type: 'inverter_fault', severity: 'alarm', stationId: 'tabaki', stationName: 'Табаки', device: 'INV-03', message: 'Аварія інвертора (fault code 0x1A)', status: 'active' },
    { id: 'ev-111', ts: iso(now, 120), type: 'inverter_fault', severity: 'warning', stationId: 'tabaki', stationName: 'Табаки', device: 'INV-07', message: 'Попередження: підвищена температура', status: 'acknowledged', ackBy: 'Кравченко' },
    // Bolgrad СКО + BESS
    { id: 'ev-120', ts: iso(now, 140), type: 'curtailment', severity: 'info', stationId: 'bolgrad', stationName: 'Болград Еліос', message: 'Активна команда СКО: обмеження 32%', status: 'active' },
    { id: 'ev-121', ts: iso(now, 138), type: 'manual', severity: 'info', stationId: 'bolgrad', stationName: 'Болград Еліос', message: 'УЗЕ переведено в заряд від PV під час обмеження', status: 'active', ackBy: 'Західенко' },
    // Nikopol operational safety
    { id: 'ev-130', ts: iso(now, 210), type: 'op_safety', severity: 'warning', stationId: 'nikopol', stationName: 'Нікополь-Еліос', message: 'Обмеження операційної безпеки 25% (тимчасова схема приєднання)', status: 'active' },
    // Cleared / historical
    { id: 'ev-140', ts: iso(now, 320), type: 'inverter_fault', severity: 'alarm', stationId: 'prometheus', stationName: 'Покров (Прометей ЕТГ)', device: 'INV-22', message: 'Аварія інвертора усунена', status: 'cleared', ackBy: 'Шевловський' },
    { id: 'ev-141', ts: iso(now, 400), type: 'planned', severity: 'info', stationId: 'prometheus', stationName: 'Покров (Прометей ЕТГ)', message: 'Планові роботи: заміна запобіжника КТП-2 завершена', status: 'cleared', ackBy: 'Шевловський' },
    { id: 'ev-142', ts: iso(now, 470), type: 'curtailment', severity: 'info', stationId: 'nickelios', stationName: 'Нікеліос', message: 'Команду СКО знято (відбій)', status: 'cleared' },
  ];
  return e;
}

export function mockSkoCommands(now: Date = new Date()): SkoCommand[] {
  return [
    { id: 'cmd-01', source: 'lms', kind: 'sko', stationId: 'prometheus', stationName: 'Покров (Прометей ЕТГ)', limitPct: 40, receivedAt: iso(now, 3), startAt: iso(now, 0), endAt: null, status: 'pending', docRef: 'РДЦ-2026/0612', compensated: true },
    { id: 'cmd-02', source: 'lms', kind: 'sko', stationId: 'bolgrad', stationName: 'Болград Еліос', limitPct: 32, receivedAt: iso(now, 140), startAt: iso(now, 135), endAt: null, status: 'executing', docRef: 'РДЦ-2026/0611', compensated: true },
    { id: 'cmd-03', source: 'phone', kind: 'op_safety', stationId: 'nikopol', stationName: 'Нікополь-Еліос', limitPct: 25, receivedAt: iso(now, 210), startAt: iso(now, 205), endAt: null, status: 'executing', fromPerson: 'диспетчер РДЦ (тел.)', compensated: false },
    { id: 'cmd-04', source: 'lms', kind: 'sko', stationId: 'nickelios', stationName: 'Нікеліос', limitPct: 50, receivedAt: iso(now, 470), startAt: iso(now, 465), endAt: iso(now, 300), status: 'completed', docRef: 'РДЦ-2026/0610', compensated: true },
  ];
}

export interface OsrEntry {
  id: string; ts: string; stationName: string; source: CmdSource;
  fromPerson: string; limitPct: number; reason: string; comment?: string;
}
